import React from 'react';
import { X, CheckCircle, AlertCircle } from 'lucide-react';
import type { FileUploadState } from '@/types/chat';
import { formatFileSize, getFileIcon } from '@/services/attachmentsApi';

interface FileUploadItemProps {
  uploadState: FileUploadState;
  onRemove: () => void;
  disabled?: boolean;
}

const CircularProgress: React.FC<{ progress: number; size?: number }> = ({
  progress,
  size = 20,
}) => {
  const strokeWidth = 2.5;
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (progress / 100) * circumference;

  return (
    <svg
      width={size}
      height={size}
      className="transform -rotate-90"
      style={{ display: 'block' }}>
      {/* Background circle */}
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke="currentColor"
        strokeWidth={strokeWidth}
        className="text-slate-200"
      />
      {/* Progress circle */}
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke="currentColor"
        strokeWidth={strokeWidth}
        strokeDasharray={circumference}
        strokeDashoffset={offset}
        strokeLinecap="round"
        className="text-[#3C3C3C] transition-all duration-300 ease-out"
      />
    </svg>
  );
};

export const FileUploadItem: React.FC<FileUploadItemProps> = ({
  uploadState,
  onRemove,
  disabled = false,
}) => {
  const { file, status, progress, error, result } = uploadState;

  const getStatusIndicator = () => {
    switch (status) {
      case 'pending':
        return (
          <div className="w-5 h-5 flex items-center justify-center">
            <div className="w-3 h-3 rounded-full bg-slate-300 animate-pulse" />
          </div>
        );
      case 'uploading':
        return <CircularProgress progress={progress} size={20} />;
      case 'completed':
        return <CheckCircle className="w-5 h-5 text-emerald-500" />;
      case 'error':
        return <AlertCircle className="w-5 h-5 text-red-500" />;
      default:
        return null;
    }
  };

  const getStatusText = () => {
    switch (status) {
      case 'pending':
        return 'Waiting...';
      case 'uploading':
        return `${progress}%`;
      case 'completed':
        return 'Uploaded';
      case 'error':
        return error || 'Upload failed';
      default:
        return '';
    }
  };

  const getBorderStyle = () => {
    switch (status) {
      case 'error':
        return 'border-red-300 bg-red-50';
      case 'completed':
        return 'border-emerald-200 bg-emerald-50/50';
      case 'uploading':
        return 'border-[#858585] bg-[#F0F0F0]';
      default:
        return 'border-slate-200 bg-white';
    }
  };

  return (
    <div
      className={`group flex items-center gap-2 px-3 py-2 rounded-lg border text-sm transition-all duration-200 ${getBorderStyle()}`}>
      {/* File icon */}
      <span className="text-base shrink-0" role="img" aria-label="file type">
        {getFileIcon(file.name)}
      </span>

      {/* File info */}
      <div className="flex flex-col min-w-0 flex-1">
        <span
          className="text-[#252F33] truncate max-w-[200px]"
          title={file.name}>
          {file.name}
        </span>
        <div className="flex items-center gap-2 text-xs">
          <span className="text-[#768D95]">{formatFileSize(file.size)}</span>
          <span
            className={`${
              status === 'error'
                ? 'text-red-500'
                : status === 'completed'
                ? 'text-emerald-600'
                : 'text-[#768D95]'
            }`}>
            {getStatusText()}
          </span>
        </div>
        {result?.file_type === 'csv' && result?.headers && (
          <span className="text-xs text-slate-500">
            CSV &middot; {result.headers.length} columns
          </span>
        )}
        {result?.file_type === 'geojson' && result?.feature_count != null && (
          <span className="text-xs text-slate-500">
            GeoJSON &middot; {result.feature_count.toLocaleString()} features
            {result.geometry_type ? ` (${result.geometry_type})` : ''}
          </span>
        )}
        {result?.file_type === 'shapefile' && (
          <span className="text-xs text-slate-500">Shapefile</span>
        )}
      </div>

      {/* Status indicator */}
      <div className="shrink-0">{getStatusIndicator()}</div>

      {/* Remove button */}
      <button
        onClick={onRemove}
        disabled={disabled || status === 'uploading'}
        className={`shrink-0 p-1 rounded-md transition-all duration-200 ${
          disabled || status === 'uploading'
            ? 'text-slate-300 cursor-not-allowed'
            : 'text-[#768D95] hover:text-red-500 hover:bg-red-50'
        }`}
        aria-label={`Remove ${file.name}`}
        title={
          status === 'uploading'
            ? 'Cannot remove while uploading'
            : `Remove ${file.name}`
        }>
        <X className="h-4 w-4" />
      </button>
    </div>
  );
};
